<template>
  <SoWidget data-testid="spayg-widget" title="Smart Pay As You Go">
    <SoSkeleton
      v-if="currentAccount.spaygEligibility.isFetching"
      class="text-h4 pb-1"
    />
    <button
      v-else
      class="show-details-btn d-flex justify-space-between align-center mt-6 w-100"
      @click="showDetails = !showDetails"
    >
      <SoStatus
        data-testid="spayg-widget-status"
        :status="accountSpaygStatus"
      />
      <SoIcon
        :icon="showDetails ? 'chevron-up' : 'chevron-down'"
        size="small"
        class="ml-2"
      />
    </button>
    <template #expand>
      <div v-show="showDetails" class="mx-6">
        <p data-testid="spayg-widget-description">
          The customer is eligible to switch to a Smart Pay As You Go tariff if
          they wish.
        </p>
        <div class="mt-5">
          <dl class="dl dl--aligned" data-testid="spayg-widget-meterpoints">
            <template
              v-for="(eligibilityData, index) in spaygEligibilityData"
              :key="index"
            >
              <dt class="font-weight-medium mr-1">
                <span v-if="eligibilityData.mpxnIdentifier"
                  >{{ getMeterpointType(eligibilityData.mpxnIdentifier) }} -
                  {{ eligibilityData.mpxnIdentifier }}
                </span>
              </dt>
              <dd>
                <SoStatus :status="getMeterpointSpaygStatus(eligibilityData)" />
              </dd>
            </template>
          </dl>
        </div>
        <div>
          <SoButton
            title="More Info"
            data-testid="spayg-widget-info-btn"
            type="secondary"
            class="mt-4 pb-6 mr-5"
            @click="showModal = true"
          />
          <SoButton
            :disabled="!hasEligibleMeterpoint"
            title="Quote & Switch"
            data-testid="spayg-widget-quote-btn"
            type="primary"
            class="mt-4 pb-6"
            @click="openSpaygFlow()"
          />
        </div>
      </div>
    </template>
  </SoWidget>
  <SpaygModal
    ref="financials-direct-debit-modal"
    v-model="showModal"
    @close-modal="showModal = false"
  />
</template>
<script lang="ts">
  import { defineComponent } from "vue"
  import SoWidget from "@/components/SoWidget.vue"
  import SoIcon from "@/components/SoIcon.vue"
  import SoSkeleton from "@/components/SoSkeleton.vue"
  import SoStatus from "@/components/SoStatus.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import { Status } from "@/types/Status"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import SpaygModal from "../modals/SpaygModal.vue"
  import { RouteNames } from "@/types/router"

  export default defineComponent({
    components: {
      SoWidget,
      SoSkeleton,
      SoIcon,
      SoButton,
      SoStatus,
      SpaygModal,
    },
    data() {
      return {
        Status,
        showDetails: false,
        showModal: false,
        currentAccount: useCurrentAccountStore(),
      }
    },
    computed: {
      spaygEligibilityData() {
        return this.currentAccount.spaygEligibility.data || []
      },
      meterpointAgreements() {
        return this.currentAccount.meterpointAgreements.data || []
      },
      meterpointDetailsData() {
        return this.currentAccount.meterpoints.data || []
      },
      hasEligibleMeterpoint() {
        return this.spaygEligibilityData.some((eligibilityData) => {
          const result =
            eligibilityData.eligibleForSpayg &&
            !this.isSpaygProcessing(eligibilityData.mpxnIdentifier || "") &&
            !this.isSpaygActive(eligibilityData.mpxnIdentifier || "")

          return result
        })
      },
      areAllMeterpointsIneligible() {
        return this.spaygEligibilityData.every((eligibilityData) => {
          return !eligibilityData.eligibleForSpayg
        })
      },
      accountSpaygStatus() {
        const hasActiveMeterpoint = this.spaygEligibilityData.some(
          (eligibilityData) => {
            return this.isSpaygActive(eligibilityData.mpxnIdentifier || "")
          },
        )
        const hasProcessingMeterpoint = this.spaygEligibilityData.some(
          (eligibilityData) => {
            return this.isSpaygProcessing(eligibilityData.mpxnIdentifier || "")
          },
        )

        if (this.areAllMeterpointsIneligible) {
          return new Status("default", "SPAYG tariff unavailable")
        }

        if (hasProcessingMeterpoint) {
          return new Status("neutral", "SPAYG tariff processing")
        }

        if (hasActiveMeterpoint) {
          return new Status("normal", "SPAYG tariff active")
        }

        return new Status("caution", "SPAYG tariff available")
      },
    },
    methods: {
      openSpaygFlow() {
        this.$router.push({ name: RouteNames.CustomerSpayg })
      },
      getMeterpointSpaygStatus(eligibilityData) {
        if (this.isSpaygProcessing(eligibilityData.mpxnIdentifier)) {
          return new Status("neutral", "Processing")
        }

        if (this.isSpaygActive(eligibilityData.mpxnIdentifier)) {
          return new Status("normal", "Active")
        }

        if (eligibilityData.eligibleForSpayg) {
          return new Status("caution", "Eligible")
        } else {
          return new Status("default", "Not Eligible")
        }
      },
      getMeterpointType(mpxnIdentifier?: string) {
        const meterpoint = this.meterpointDetailsData.find(
          (meterpointDetails) => {
            return meterpointDetails.identifier === mpxnIdentifier
          },
        )
        return meterpoint?.meterpointType
      },
      getSpaygAgreement(mpxnIdentifier?: string) {
        const meterpoint = this.meterpointDetailsData.find(
          (meterpointDetails) =>
            meterpointDetails.identifier === mpxnIdentifier,
        )
        const agreements =
          this.meterpointAgreements[meterpoint?.identifier || ""]
        const agreement = agreements?.find(
          (agreement) => agreement.isSmartPayAsYouGo,
        )
        return agreement
      },
      isSpaygProcessing(mpxnIdentifier?: string) {
        const agreement = this.getSpaygAgreement(mpxnIdentifier)
        return agreement?.isSmartPayAsYouGo && agreement.isPending
      },
      isSpaygActive(mpxnIdentifier?: string) {
        const agreement = this.getSpaygAgreement(mpxnIdentifier)
        return (
          agreement?.isSmartPayAsYouGo &&
          agreement.isActive &&
          !agreement.isPending
        )
      },
    },
  })
</script>
